/**
 * Test kit Rebuy guard: stops `.rebuy-button` cart adds for products tagged
 * `test-kits` and routes the shopper into test kit registration instead.
 *
 * Rebuy dispatches `rebuy.beforeAdd` with `cancelable: false`, so the add
 * cannot be stopped from that event. The click is intercepted in the capture
 * phase instead, which runs before Rebuy's own button handler.
 */
(function () {
  "use strict";

  const TEST_KIT_TAG = "test-kits";
  const DIALOG_ID = "test-kit-registration-dialog";
  const VIEW_REGISTRATION = "test-kit-registration";
  const SEL_BUTTON = ".rebuy-button";
  const SEL_QUICK_VIEW = ".product-quick-view-button";
  const SEL_PRODUCT_BLOCK = '[class*="product-id-"]';
  const SEL_MODAL = ".rebuy-modal";
  const SEL_MODAL_CLOSE = ".rebuy-modal.is-visible .rebuy-modal__close";

  const NO_PRODUCT = {
    id: null,
    handle: null,
    variantId: null,
    isTestKit: false,
  };

  /**
   * Rebuy's quick view modal renders no product identifiers, so the product of
   * the widget item that opened it is kept for the add click inside it.
   *
   * @type {typeof NO_PRODUCT}
   */
  let quickViewProduct = NO_PRODUCT;

  /**
   * Shared by every click so concurrent adds cannot insert a second dialog or
   * load the script twice.
   *
   * @type {Promise<void>|null}
   */
  let registrationLoad = null;

  // A promise cannot report whether it settled, so readiness is tracked.
  let injectedRegistrationReady = false;

  /**
   * @param {string|string[]} tags product tags, comma separated when a string
   * @returns {boolean}
   */
  function hasTestKitTag(tags) {
    if (Array.isArray(tags)) {
      return tags.some(
        (tag) => String(tag).trim().toLowerCase() === TEST_KIT_TAG,
      );
    }

    if (typeof tags === "string") {
      return tags
        .split(",")
        .some((tag) => tag.trim().toLowerCase() === TEST_KIT_TAG);
    }

    return false;
  }

  /**
   * Rebuy keeps every rendered product, tags included, on its widget
   * instances. Reading from there keeps the test kit check synchronous, which
   * the capture phase requires.
   *
   * @param {string|null} productId
   * @returns {object|null}
   */
  function findRebuyProduct(productId) {
    if (!productId || !window.Rebuy) {
      return null;
    }

    const widgets = window.Rebuy.widgets || {};
    const products = Object.keys(widgets)
      .map((key) => {
        const data = widgets[key] && widgets[key].data;
        return data && Array.isArray(data.products) ? data.products : [];
      })
      .flat();

    return (
      products.find((product) => String(product.id) === String(productId)) ||
      null
    );
  }

  /**
   * @param {Element} block Rebuy product block
   * @returns {string|null}
   */
  function getBlockHandle(block) {
    const link = block.querySelector('a[href*="/products/"]');
    const match = (link ? link.getAttribute("href") : "").match(
      /\/products\/([^/?#]+)/,
    );
    return match ? match[1] : null;
  }

  /**
   * @param {Element} button clicked Rebuy button
   * @returns {typeof NO_PRODUCT}
   */
  function resolveProduct(button) {
    const block = button.closest(SEL_PRODUCT_BLOCK);
    if (!block) {
      return button.closest(SEL_MODAL) ? quickViewProduct : NO_PRODUCT;
    }

    const id = (block.className.match(/product-id-(\d+)/) || [])[1] || null;
    const product = findRebuyProduct(id);
    const variants =
      product && Array.isArray(product.variants) ? product.variants : [];

    return {
      id,
      handle: (product && product.handle) || getBlockHandle(block),
      variantId:
        (product && product.selected_variant_id) ||
        (variants[0] && variants[0].id) ||
        null,
      isTestKit: product
        ? hasTestKitTag(product.tags)
        : block.classList.contains(`tag-${TEST_KIT_TAG}`),
    };
  }

  /**
   * Used only when the form cannot be loaded into the current page, so a test
   * kit never reaches the cart unregistered.
   *
   * @param {typeof NO_PRODUCT} product
   * @returns {string}
   */
  function buildRegistrationUrl(product) {
    const url = new URL(`/products/${product.handle}`, window.location.origin);
    url.searchParams.set("test_kit_register", "1");

    if (product.variantId) {
      url.searchParams.set("variant", String(product.variantId));
    }

    return url.toString();
  }

  /**
   * @param {object} detail passed to `test-kit-registration.js`
   */
  function requestRegistration(detail) {
    document.dispatchEvent(
      new CustomEvent("test-kit:register", { detail: detail }),
    );
  }

  /**
   * @returns {boolean} true when the page rendered the form itself
   */
  function isRegistrationRendered() {
    return (
      document.getElementById(DIALOG_ID) != null &&
      window.testKitRegistrationConfig != null
    );
  }

  /**
   * @returns {boolean} true when a dispatched request will be handled
   */
  function canRequestRegistration() {
    if (!isRegistrationRendered()) {
      return false;
    }

    return registrationLoad === null || injectedRegistrationReady;
  }

  /**
   * @param {typeof NO_PRODUCT} product
   * @returns {boolean} true when the page already holds this kit's form
   */
  function isRenderedProduct(product) {
    return (
      canRequestRegistration() &&
      String(window.testKitRegistrationConfig.productId) === String(product.id)
    );
  }

  /**
   * @param {object} payload registration payload for any test kit
   * @returns {Promise<void>}
   */
  function loadRegistration(payload) {
    if (!registrationLoad) {
      document.body.insertAdjacentHTML("beforeend", payload.dialog);
      window.testKitRegistrationConfig = payload.config;

      registrationLoad = Promise.all([
        loadAsset("link", { rel: "stylesheet", href: payload.styles }),
        loadAsset("script", { src: payload.script }),
      ]).then(() => {
        injectedRegistrationReady = true;
      });
    }

    return registrationLoad;
  }

  /**
   * @param {"link"|"script"} tag
   * @param {Record<string, string>} attributes
   * @returns {Promise<void>}
   */
  function loadAsset(tag, attributes) {
    return new Promise((resolve, reject) => {
      const element = document.createElement(tag);
      Object.keys(attributes).forEach((name) => {
        element.setAttribute(name, attributes[name]);
      });
      element.addEventListener("load", () => resolve());
      element.addEventListener("error", () => reject(new Error(tag)));
      document.head.appendChild(element);
    });
  }

  /**
   * Loads the registration form into the current page. Its markup, styles and
   * script are otherwise rendered on test kit product pages only.
   *
   * @param {typeof NO_PRODUCT} product
   * @returns {Promise<void>}
   */
  async function openRegistrationInPage(product, extraDetail) {
    const response = await fetch(
      `/products/${product.handle}?view=${VIEW_REGISTRATION}`,
    );
    if (!response.ok) {
      throw new Error("registration payload");
    }

    const payload = await response.json();
    const config = payload.config;
    if (product.variantId) {
      config.defaultVariantId = product.variantId;
    }

    if (!canRequestRegistration()) {
      await loadRegistration(payload);
    }

    requestRegistration(
      Object.assign({ config: config }, extraDetail),
    );
  }

  /**
   * @param {typeof NO_PRODUCT} product
   * @returns {boolean} true when registration can be reached for this kit
   */
  function canRegister(product) {
    return product.handle != null || isRenderedProduct(product);
  }

  /**
   * Single entry point for both Rebuy paths, so the kit being registered
   * always brings its own variant, age rule and title.
   *
   * @param {typeof NO_PRODUCT} product
   * @param {object} [extraDetail] extra detail for `test-kit-registration.js`
   */
  function startRegistration(product, extraDetail) {
    if (isRenderedProduct(product)) {
      requestRegistration(
        Object.assign({ variantId: product.variantId }, extraDetail),
      );
      return;
    }

    openRegistrationInPage(product, extraDetail).catch(() => {
      window.location.assign(buildRegistrationUrl(product));
    });
  }

  /**
   * @param {MouseEvent} event
   */
  function onButtonClickCapture(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const button = event.target.closest(SEL_BUTTON);
    if (!button) {
      return;
    }

    if (button.matches(SEL_QUICK_VIEW)) {
      quickViewProduct = resolveProduct(button);
      return;
    }

    const product = resolveProduct(button);
    if (!product.isTestKit) {
      return;
    }

    if (!canRegister(product)) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    // The quick view has served its purpose once registration takes over.
    document.querySelector(SEL_MODAL_CLOSE)?.click();

    startRegistration(product);
  }

  /**
   * Safety net for a test kit that reached the cart anyway, from a Rebuy
   * surface the click guard does not cover. The unregistered line is dropped
   * by `test-kit-registration.js` once the form is ready.
   *
   * @param {CustomEvent} event
   */
  function onRebuyAdd(event) {
    const payload = event && event.detail ? event.detail.product : null;
    if (!payload || !hasTestKitTag(payload.tags)) {
      return;
    }

    const product = {
      id: payload.id || payload.product_id || null,
      handle: payload.handle || null,
      variantId: getPayloadVariantId(payload),
      isTestKit: true,
    };

    if (!canRegister(product)) {
      return;
    }

    startRegistration(product, {
      unregisteredVariantId: product.variantId,
    });
  }

  /**
   * @param {object} payload Rebuy product payload
   * @returns {number|null}
   */
  function getPayloadVariantId(payload) {
    const raw =
      payload.selected_variant_id ||
      payload.variant_id ||
      (payload.selected_variant && payload.selected_variant.id) ||
      null;

    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  document.addEventListener("click", onButtonClickCapture, true);
  document.addEventListener("rebuy.add", onRebuyAdd);
})();
